import type { AuthAccount, AuthRole } from "../domain/auth";
import {
  ageForGrade,
  formatDateLabel,
  schools,
  type AgeFilter,
  type EventKind,
  type EventStatus,
  type LiftEvent,
  type Registration,
  type RegistrationStatus,
  type School,
  type Student,
} from "../domain/data";
import type { FormQuestion, QuestionKind } from "../domain/questions";
import { normalizeQuestion } from "../domain/questions";

export const SCHOOL_ID: Record<School, string> = {
  "Suttons Bay": "suttons-bay",
  Northport: "northport",
  Leland: "leland",
  "Glen Lake": "glen-lake",
};

const SCHOOL_BY_ID: Record<string, School> = {
  "suttons-bay": "Suttons Bay",
  northport: "Northport",
  leland: "Leland",
  "glen-lake": "Glen Lake",
};

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID.test(value);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function schoolId(school: School): string {
  return SCHOOL_ID[school];
}

export function schoolFromId(id: string): School | null {
  return SCHOOL_BY_ID[id] ?? null;
}

export function schoolsToIds(value: School[] | "all"): string[] {
  const list = value === "all" ? schools : value;
  return list.map((school) => SCHOOL_ID[school]);
}

/** Empty or the full school list means every Lift school. */
export function idsToSchools(ids: string[]): School[] | "all" {
  const names = ids
    .map((id) => SCHOOL_BY_ID[id])
    .filter((school): school is School => Boolean(school));
  if (names.length === 0 || names.length === schools.length) return "all";
  return names;
}

export function questionsFromRows(rows: unknown[]): FormQuestion[] {
  return rows
    .filter(isRecord)
    .map((row) => {
      const id = text(row.id);
      const label = text(row.label);
      if (!id) return null;
      return normalizeQuestion({
        id,
        label,
        labelEs: text(row.label_es),
        kind: questionKind(row.kind),
        required: row.required === true,
        options: textList(row.options),
        optionsEs: textList(row.options_es),
      });
    })
    .filter((item): item is FormQuestion => item !== null);
}

export function questionKind(value: unknown): QuestionKind {
  if (
    value === "text" ||
    value === "paragraph" ||
    value === "yesno" ||
    value === "radio" ||
    value === "checkboxes" ||
    value === "dropdown"
  ) {
    return value;
  }
  return "text";
}

export type EventRow = {
  id: string;
  slug: string;
  name: string;
  starts_on: string;
  time_label: string;
  location: string;
  capacity: number;
  kind: string;
  status: string;
  waitlist_enabled: boolean;
  school_ids: string[];
  age_filter: string;
  description: string;
  description_es: string;
};

export function eventFromRow(row: EventRow, questions: FormQuestion[]): LiftEvent | null {
  if (!isUuid(row.id) || !row.name || !row.starts_on) return null;
  const kind: EventKind = row.kind === "program" ? "program" : "rsvp";
  const status: EventStatus = row.status === "archived" ? "archived" : "open";
  return {
    id: row.id,
    slug: row.slug || row.id,
    name: row.name,
    dateIso: row.starts_on.slice(0, 10),
    dateLabel: formatDateLabel(row.starts_on.slice(0, 10)),
    timeLabel: row.time_label ?? "",
    location: row.location ?? "",
    capacity: row.capacity,
    registered: 0,
    schools: idsToSchools(row.school_ids ?? []),
    age: ageFilter(row.age_filter),
    status,
    kind,
    waitlistEnabled: row.waitlist_enabled,
    questions,
    description: row.description ?? "",
    descriptionEs: row.description_es ?? "",
  };
}

export function eventToRow(event: LiftEvent): EventRow {
  return {
    id: event.id,
    slug: event.slug || event.id,
    name: event.name,
    starts_on: event.dateIso,
    time_label: event.timeLabel,
    location: event.location,
    capacity: event.capacity,
    kind: event.kind,
    status: event.status,
    waitlist_enabled: event.waitlistEnabled,
    school_ids: schoolsToIds(event.schools),
    age_filter: event.age,
    description: event.description,
    description_es: event.descriptionEs,
  };
}

export type StudentRow = {
  id: string;
  household_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  school_id: string;
  grade: number;
  birthday: string | null;
  guardian_name: string;
  guardian_email: string;
  guardian_phone: string;
  emergency_name: string;
  emergency_phone: string;
  acceptance?: string;
};

export function studentFromRow(row: StudentRow): Student | null {
  const school = schoolFromId(row.school_id);
  if (!isUuid(row.id) || !school) return null;
  const grade = row.grade;
  return {
    id: row.id,
    given: row.first_name,
    family: row.last_name,
    email: row.email,
    school,
    grade,
    age: ageForGrade(grade),
    birthday: row.birthday?.slice(0, 10) ?? "",
    guardianName: row.guardian_name,
    guardianEmail: row.guardian_email,
    guardianPhone: row.guardian_phone,
    emergencyName: row.emergency_name,
    emergencyPhone: row.emergency_phone,
    acceptance:
      row.acceptance === "pending" || row.acceptance === "declined"
        ? row.acceptance
        : "accepted",
  };
}

export function studentToRow(
  student: Student,
  householdId: string | null,
): StudentRow {
  return {
    id: student.id,
    household_id: householdId,
    first_name: student.given,
    last_name: student.family,
    email: student.email,
    school_id: schoolId(student.school),
    grade: student.grade,
    birthday: student.birthday || null,
    guardian_name: student.guardianName,
    guardian_email: student.guardianEmail,
    guardian_phone: student.guardianPhone,
    emergency_name: student.emergencyName,
    emergency_phone: student.emergencyPhone,
  };
}

export type RegistrationRow = {
  id: string;
  event_id: string;
  student_id: string;
  status: string;
  registered_at: string;
  waitlisted_at: string | null;
  cancelled_at: string | null;
  checked_in_at: string | null;
  checked_in_by: string | null;
  answers: unknown;
};

export function registrationFromRow(row: RegistrationRow): Registration | null {
  if (!isUuid(row.id) || !isUuid(row.event_id) || !isUuid(row.student_id)) {
    return null;
  }
  return {
    id: row.id,
    eventId: row.event_id,
    studentId: row.student_id,
    status: registrationStatus(row.status),
    registeredAt: row.registered_at,
    waitlistedAt: row.waitlisted_at,
    cancelledAt: row.cancelled_at,
    checkedInAt: row.checked_in_at,
    checkedInBy: row.checked_in_by,
    answers: stringRecord(row.answers),
  };
}

export type ProfileRole = "admin" | "staff" | "student" | "parent";

/** The app has one staff destination. Admin stays a database role. */
export function appRole(role: ProfileRole): AuthRole {
  if (role === "admin" || role === "staff") return "staff";
  if (role === "student") return "student";
  return "parent";
}

export function accountFromProfile(input: {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: ProfileRole;
  studentIds: string[];
  ownStudentId: string | null;
  mustResetPassword?: boolean;
  pendingStudentIds?: string[];
}): AuthAccount {
  const role = appRole(input.role);
  const pending = input.pendingStudentIds ?? [];
  return {
    email: input.email.trim().toLowerCase(),
    password: "",
    role,
    given: input.firstName.trim() || "Parent",
    family: input.lastName.trim() || "Family",
    phone: input.phone?.trim() ?? "",
    studentId: role === "student" ? input.ownStudentId : null,
    studentIds:
      role === "parent"
        ? input.studentIds.filter((id) => !pending.includes(id))
        : input.ownStudentId
          ? [input.ownStudentId]
          : [],
    staffId: role === "staff" ? "staff" : null,
    mustResetPassword: input.mustResetPassword === true,
    pendingStudentIds: role === "parent" ? pending : [],
  };
}

export function stringRecord(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {};
  const next: Record<string, string> = {};
  Object.entries(value).forEach(([key, item]) => {
    if (typeof item === "string") next[key] = item;
    else if (typeof item === "number" || typeof item === "boolean") {
      next[key] = String(item);
    }
  });
  return next;
}

function registrationStatus(value: string): RegistrationStatus {
  if (value === "waitlisted" || value === "cancelled") return value;
  return "registered";
}

function ageFilter(value: string): AgeFilter {
  if (value === "ms" || value === "hs") return value;
  return "all";
}

export function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function parseEventRow(value: unknown): EventRow | null {
  if (!isRecord(value) || typeof value.id !== "string") return null;
  return {
    id: value.id,
    slug: text(value.slug),
    name: text(value.name),
    starts_on: text(value.starts_on),
    time_label: text(value.time_label),
    location: text(value.location),
    capacity: typeof value.capacity === "number" ? value.capacity : 0,
    kind: text(value.kind),
    status: text(value.status),
    waitlist_enabled: value.waitlist_enabled !== false,
    school_ids: textList(value.school_ids),
    age_filter: text(value.age_filter),
    description: text(value.description),
    description_es: text(value.description_es),
  };
}

export function parseStudentRow(value: unknown): StudentRow | null {
  if (!isRecord(value) || typeof value.id !== "string") return null;
  return {
    id: value.id,
    household_id: typeof value.household_id === "string" ? value.household_id : null,
    first_name: text(value.first_name),
    last_name: text(value.last_name),
    email: text(value.email),
    school_id: text(value.school_id),
    grade: typeof value.grade === "number" ? value.grade : 0,
    birthday: typeof value.birthday === "string" ? value.birthday : null,
    guardian_name: text(value.guardian_name),
    guardian_email: text(value.guardian_email),
    guardian_phone: text(value.guardian_phone),
    emergency_name: text(value.emergency_name),
    emergency_phone: text(value.emergency_phone),
    acceptance: text(value.acceptance),
  };
}

export function parseRegistrationRow(value: unknown): RegistrationRow | null {
  if (!isRecord(value) || typeof value.id !== "string") return null;
  return {
    id: value.id,
    event_id: text(value.event_id),
    student_id: text(value.student_id),
    status: text(value.status),
    registered_at: text(value.registered_at),
    waitlisted_at: typeof value.waitlisted_at === "string" ? value.waitlisted_at : null,
    cancelled_at: typeof value.cancelled_at === "string" ? value.cancelled_at : null,
    checked_in_at: typeof value.checked_in_at === "string" ? value.checked_in_at : null,
    checked_in_by: typeof value.checked_in_by === "string" ? value.checked_in_by : null,
    answers: value.answers,
  };
}

function textList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}
